import React, { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { Pencil, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';

interface NotesSectionProps {
  notes: string | null | undefined;
  onSave: (notes: string) => Promise<void>;
  saving?: boolean;
}

const EDITOR_CSS = `
  /* Editor chrome */
  .notes-editor .w-md-editor {
    background: var(--glass-bg-card);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: var(--radius-card);
    box-shadow: none;
    --color-canvas-default: var(--glass-bg-card);
    --color-border-default: rgba(255,255,255,0.10);
  }
  .notes-editor .w-md-editor-toolbar {
    background: var(--glass-bg-card);
    border-bottom: 1px solid rgba(255,255,255,0.10);
    border-radius: var(--radius-card) var(--radius-card) 0 0;
  }
  .notes-editor .w-md-editor-toolbar li button {
    color: var(--color-text-muted);
  }
  .notes-editor .w-md-editor-toolbar li button:hover,
  .notes-editor .w-md-editor-toolbar li.active button {
    color: var(--color-text);
    background: rgba(255,255,255,0.06);
  }
  .notes-editor .w-md-editor-toolbar li button svg {
    fill: currentColor;
  }
  .notes-editor .w-md-editor-text {
    padding: 0;
  }
  /* CRITICAL: textarea must have explicit contrasting colors — beat all themes */
  .notes-editor .w-md-editor-text-input,
  .notes-editor .w-md-editor-text textarea,
  .notes-editor textarea.w-md-editor-text-input {
    color: var(--color-text) !important;
    background-color: var(--color-surface) !important;
    caret-color: var(--color-text);
    -webkit-text-fill-color: var(--color-text) !important;
  }
  .notes-editor .w-md-editor-text-input::placeholder,
  .notes-editor .w-md-editor-text textarea::placeholder {
    color: var(--color-text-faint) !important;
  }
  .notes-editor .w-md-editor-text-pre {
    color: var(--color-text-faint) !important;
    background-color: var(--color-surface) !important;
  }
  .notes-editor .w-md-editor-text .wmde-markdown {
    color: var(--color-text-faint);
    background: var(--glass-bg-card);
  }
  /* Preview panel (hidden with preview="edit", but safe) */
  .notes-editor .w-md-editor-preview {
    background: var(--glass-bg-card);
    box-shadow: inset 1px 0 0 0 rgba(255,255,255,0.10);
    padding: 1rem;
  }
  /* Markdown rendered content */
  .notes-editor .wmde-markdown,
  .notes-preview .wmde-markdown {
    background: transparent;
    color: var(--color-text);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  }
  .notes-preview .wmde-markdown { font-size: var(--text-sm); }
  .notes-editor .wmde-markdown h1, .notes-editor .wmde-markdown h2,
  .notes-editor .wmde-markdown h3, .notes-editor .wmde-markdown h4,
  .notes-preview .wmde-markdown h1, .notes-preview .wmde-markdown h2,
  .notes-preview .wmde-markdown h3, .notes-preview .wmde-markdown h4 {
    color: var(--color-text);
    border-bottom-color: rgba(255,255,255,0.10);
  }
  .notes-editor .wmde-markdown code,
  .notes-preview .wmde-markdown code {
    background: rgba(255,255,255,0.06);
    color: var(--color-text);
  }
  .notes-editor .wmde-markdown pre,
  .notes-preview .wmde-markdown pre {
    background: rgba(255,255,255,0.06);
  }
  .notes-editor .wmde-markdown blockquote,
  .notes-preview .wmde-markdown blockquote {
    border-left-color: var(--color-primary);
    color: var(--color-text-muted);
  }
  .notes-editor .wmde-markdown table th,
  .notes-editor .wmde-markdown table td,
  .notes-preview .wmde-markdown table th,
  .notes-preview .wmde-markdown table td {
    border-color: rgba(255,255,255,0.10);
  }
  .notes-editor .wmde-markdown table th,
  .notes-preview .wmde-markdown table th {
    background: rgba(255,255,255,0.06);
  }
  .notes-editor .wmde-markdown a,
  .notes-preview .wmde-markdown a {
    color: var(--color-primary);
  }
  .notes-editor .wmde-markdown ul,
  .notes-editor .wmde-markdown ol,
  .notes-preview .wmde-markdown ul,
  .notes-preview .wmde-markdown ol {
    color: var(--color-text);
  }
`;

export default function NotesSection({ notes, onSave, saving }: NotesSectionProps) {
  const [editing, setEditing] = useState(!notes);
  const [draft, setDraft] = useState(notes ?? '');

  const handleSave = async () => {
    await onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(notes ?? '');
    setEditing(false);
  };

  return (
    <>
      <style>{EDITOR_CSS}</style>
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        marginTop: '1.5rem',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: editing ? '1px solid rgba(255,255,255,0.10)' : 'none',
        }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, margin: 0 }}>Note</h3>
          {editing ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                <X size={14} style={{ marginRight: '0.25rem' }} /> Annulla
              </Button>
              <Button size="sm" onClick={handleSave} loading={saving}>
                <Save size={14} style={{ marginRight: '0.25rem' }} /> Salva
              </Button>
            </div>
          ) : (
            notes ? (
              <Button size="sm" variant="ghost" onClick={() => { setDraft(notes ?? ''); setEditing(true); }}>
                <Pencil size={14} style={{ marginRight: '0.25rem' }} /> Modifica
              </Button>
            ) : null
          )}
        </div>

        {/* Content */}
        <div style={{ padding: editing ? '1.25rem' : '0 1.25rem 1.25rem' }}>
          {editing ? (
            <div className="notes-editor">
              <MDEditor
                value={draft}
                onChange={val => setDraft(val || '')}
                height={300}
                preview="edit"
                visibleDragbar={false}
                hideToolbar={false}
                data-color-mode="dark"
              />
            </div>
          ) : (
            notes ? (
              <div className="notes-preview" style={{ minHeight: 60 }}>
                <MDEditor.Markdown
                  source={notes}
                  style={{ padding: '0.5rem 0', whiteSpace: 'pre-wrap' }}
                  data-color-mode="dark"
                />
              </div>
            ) : (
              <button
                onClick={() => { setDraft(''); setEditing(true); }}
                style={{
                  width: '100%', padding: '2rem', textAlign: 'center',
                  background: 'none', border: '2px dashed rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-card)', cursor: 'pointer',
                  color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)',
                }}
              >
                <Pencil size={16} style={{ marginBottom: '0.25rem', display: 'block', margin: '0 auto 0.5rem' }} />
                Aggiungi note al progetto...
              </button>
            )
          )}
        </div>
      </div>
    </>
  );
}
